import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
import styles from './ProfilePage.module.css';
import apiRequest from '../../lib/apiRequest';
import logger from '../../lib/logger';

const ProfilePage = () => {
  const { id } = useParams(); // Profile id from URL
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // State for user info (username and email)
  const [userInfo, setUserInfo] = useState(null);
  // State for uploaded videos
  const [videos, setVideos] = useState([]);
  // State for liked videos (fetched from profile API)
  const [likedVideos, setLikedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch profile data on mount (user info, uploaded videos, and liked videos)
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Assume your endpoint returns { success: true, user: { username, email, ... }, videos: [...], likedVideos: [...] }
        const res = await apiRequest(`/users/${id}`);
        if (res.data.success) {
          setUserInfo({
            username: res.data.user.username,
            email: res.data.user.email,
          });
          setVideos(res.data.videos || []);
          setLikedVideos(res.data.likedVideos || []);
        } else {
          setError(res.data.message || 'Failed to load profile.');
        }
      } catch (err) {
        logger.error('Error fetching profile:', err);
        setError('Could not load profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  // Handler to delete a video by ID (only for owner)
  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await apiRequest.delete(`/videos/selfieVideos/${videoId}`);
      // Remove deleted video from state
      setVideos(videos.filter((video) => video._id !== videoId));
    } catch (error) {
      logger.error('Error deleting video:', error);
      alert(error.response?.data?.message || 'Failed to delete video.');
    }
  };

  // Helper to render a video card
  const renderVideoCard = (video, showActions = false) => (
    <div key={video._id} className={styles.videoCardContainer}>
      <Link to={`/selfieVideos/${video._id}`} className={styles.videoCardLink}>
        <div className={styles.videoCard}>
          <div className={styles.thumbnailWrapper}>
            <img
              src={video.thumbnailCdnUrl || 'default-thumb.jpg'}
              alt={video.title}
              className={styles.videoThumbnail}
            />
            {video.visibility && (
              <div
                className={`${styles.visibilityBadge} ${
                  video.visibility === 'private' ? styles.private : styles.public
                }`}
              >
                {video.visibility.charAt(0).toUpperCase() + video.visibility.slice(1)}
              </div>
            )}
          </div>
          <div className={styles.videoInfo}>
            <h5 className={styles.videoTitle}>{video.title}</h5>
            <p className={styles.videoViews}>{video.views || 0} views</p>
          </div>
        </div>
      </Link>
      {showActions && (
        <div className={styles.actionButtons}>
          <button
            className={styles.editButton}
            onClick={() => navigate(`/selfieVideos/edit/${video._id}`)}
          >
            Edit
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => handleDelete(video._id)}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );

  if (loading) return <div className={styles.loadingMsg}>Loading profile...</div>;
  if (error) return <div className={styles.errorMsg}>{error}</div>;
  if (!userInfo) return <div className={styles.errorMsg}>User not found.</div>;

  return (
    <div className={styles.profileContainer}>
      {/* User Info Section */}
      <div className={styles.userInfo}>
        <h2 className={styles.userName}>{userInfo.username}</h2>
        {userInfo.email && (
          <p className={styles.userEmail}>
            <span className={styles.mailIcon}>📧</span> {userInfo.email}
          </p>
        )}
        {user && user._id === id && (
          <button
            className={styles.editProfileButton}
            onClick={() => navigate(`/profile/edit/${id}`)}
          >
            Edit Profile
          </button>
        )}
      </div>

      {/* Uploaded Videos Section */}
      <div className={styles.videosSection}>
        <h3 className={styles.videosTitle}>Uploaded Videos</h3>
        {videos.length === 0 ? (
          <p className={styles.noVideos}>No videos found for this user.</p>
        ) : (
          <div className={styles.videoList}>
            {videos.map((video) => renderVideoCard(video, user && user._id === id))}
          </div>
        )}
      </div>

      {/* Liked Videos Section (only for the profile owner) */}
    
        <div className={styles.videosSection}>
          <h3 className={styles.videosTitle}>Liked Videos</h3>
          {likedVideos.length === 0 ? (
            <p className={styles.noVideos}>You haven't liked any videos yet.</p>
          ) : (
            <div className={styles.videoList}>
              {likedVideos.map((video) => renderVideoCard(video, false))}
            </div>
          )}
        </div>
   
    </div>
  );
};

export default ProfilePage;
